import { computed, ref } from 'vue'
import storage from 'tlv-auth/src/services/Storage.service'
import {
  getAccessRefreshTokenByAuthorizationCode,
  refreshTokensCall,
  signOutUser
} from 'tlv-auth/src/services/api/Auth'
import {
  AUTH_CONFIG,
  getAuthStorageRevision,
  setAuthStorageNamespace
} from 'tlv-auth/src/services/auth/Config.js'
import { v4 as uuidv4 } from 'uuid'
import {
  APP_TYPE_QUERY_PARAM,
  DEFAULT_APP_TYPE,
  appType,
  getRedirectUrl,
  setAppType
} from 'tlv-auth/src/comp/AppType'
import { logAuthEvent, sanitizeUrlForLog, summarizeErrorForLog } from '../debug/authDebug'

export const sleep = (time = 0) => new Promise((resolve) => setTimeout(resolve, Math.ceil(time)))

const AUTH_HOOKS = {
  afterLogin: {},
  beforeLogout: {},
  afterLogout: {},
  onTokenRefreshFailed: {}
}

const isInstalled = ref(false)
const isLoggedIn = ref(false)
const isLoading = ref(true)
const hasError = ref(false)
const errorMessage = ref('')
let tokenRefreshFlight = null
let tokenRevision = 0
let authSessionRevision = 0
let logoutSession = null

// Request ownership follows login identity, while token rotation remains in the same session.
const getSessionId = () => `${authSessionRevision}:${getAuthStorageRevision()}`

const hasValidTokenPair = (tokens) =>
  typeof tokens?.accessToken === 'string' &&
  tokens.accessToken.trim().length > 0 &&
  typeof tokens?.refreshToken === 'string' &&
  tokens.refreshToken.trim().length > 0

const captureTokenSession = () => ({
  sessionId: getSessionId(),
  revision: tokenRevision,
  accessTokenKey: AUTH_CONFIG.accessTokenStorageKey,
  refreshTokenKey: AUTH_CONFIG.refreshTokenStorageKey,
  accessToken: storage.getItem(AUTH_CONFIG.accessTokenStorageKey),
  refreshToken: storage.getItem(AUTH_CONFIG.refreshTokenStorageKey)
})

const isCurrentTokenSession = (session) => {
  const current = captureTokenSession()
  return Object.keys(session).every((key) => session[key] === current[key])
}

const hasStoredCredentials = () =>
  Boolean(
    storage.getItem(AUTH_CONFIG.accessTokenStorageKey) ||
    storage.getItem(AUTH_CONFIG.refreshTokenStorageKey)
  )

const isInterruptedRefreshResponse = (error, aborted) =>
  aborted?.value === true ||
  ['AbortError', 'TimeoutError', 'SyntaxError', 'TypeError'].includes(error?.value?.name)

const hasOwnConfig = (config, key) => Object.prototype.hasOwnProperty.call(config, key)

const decodeBase64UrlToText = (value = '') => {
  const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/')
  const paddingLength = (4 - (normalized.length % 4 || 4)) % 4
  const paddedValue = normalized + '='.repeat(paddingLength)

  if (typeof globalThis.atob === 'function') {
    const binaryValue = globalThis.atob(paddedValue)
    if (typeof globalThis.TextDecoder === 'function') {
      const bytes = Uint8Array.from(binaryValue, (char) => char.charCodeAt(0))
      return new globalThis.TextDecoder().decode(bytes)
    }
    return binaryValue
  }

  if (typeof globalThis.Buffer !== 'undefined') {
    return globalThis.Buffer.from(paddedValue, 'base64').toString('utf8')
  }

  throw new Error('No base64 decoder available')
}

const parseJwtPayload = (token) => {
  const [, payload = ''] = String(token || '').split('.')
  if (!payload) return null

  try {
    return JSON.parse(decodeBase64UrlToText(payload))
  } catch {
    return null
  }
}

const getJwtExpirationMs = (token) => {
  const exp = Number(parseJwtPayload(token)?.exp)
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
}

const buildTokenMetadataForLog = (token) => {
  const expiresAtMs = getJwtExpirationMs(token)

  return {
    present: Boolean(token),
    expiresAtMs,
    expiresInMs: Number.isFinite(expiresAtMs) ? Math.max(expiresAtMs - Date.now(), 0) : null
  }
}

const AUTH_TIMESTAMP_VALIDATION_MODES = Object.freeze({
  OFF: 'off',
  ON: 'on',
  LOG_ONLY: 'log-only'
})

const normalizeTimestampValidationMode = (value) => {
  if (value === true || value === 1 || value === '1') {
    return AUTH_TIMESTAMP_VALIDATION_MODES.ON
  }

  if (value === false || value === 0 || value === '0' || value == null) {
    return AUTH_TIMESTAMP_VALIDATION_MODES.OFF
  }

  if (typeof value !== 'string') {
    return AUTH_TIMESTAMP_VALIDATION_MODES.OFF
  }

  const normalizedValue = value.trim().toLowerCase()

  if (!normalizedValue) {
    return AUTH_TIMESTAMP_VALIDATION_MODES.OFF
  }

  if (
    normalizedValue === 'true' ||
    normalizedValue === 'yes' ||
    normalizedValue === 'y' ||
    normalizedValue === AUTH_TIMESTAMP_VALIDATION_MODES.ON
  ) {
    return AUTH_TIMESTAMP_VALIDATION_MODES.ON
  }

  if (
    normalizedValue === 'false' ||
    normalizedValue === 'no' ||
    normalizedValue === 'n' ||
    normalizedValue === AUTH_TIMESTAMP_VALIDATION_MODES.OFF
  ) {
    return AUTH_TIMESTAMP_VALIDATION_MODES.OFF
  }

  if (
    normalizedValue === AUTH_TIMESTAMP_VALIDATION_MODES.LOG_ONLY ||
    normalizedValue === 'log_only' ||
    normalizedValue === 'logonly'
  ) {
    return AUTH_TIMESTAMP_VALIDATION_MODES.LOG_ONLY
  }

  return AUTH_TIMESTAMP_VALIDATION_MODES.OFF
}

const getTimestampValidationMode = () =>
  normalizeTimestampValidationMode(AUTH_CONFIG.timestampValidationMode)

const getAccessTokenTimestampValidation = (bufferMs = 0) => {
  const mode = getTimestampValidationMode()
  const expiresAtMs = getJwtExpirationMs(storage.getItem(AUTH_CONFIG.accessTokenStorageKey))
  const safeBufferMs = Number.isFinite(bufferMs) && bufferMs > 0 ? bufferMs : 0
  const isWithinRefreshWindow =
    Number.isFinite(expiresAtMs) && expiresAtMs > 0
      ? Date.now() >= expiresAtMs - safeBufferMs
      : false

  return {
    mode,
    expiresAtMs,
    isWithinRefreshWindow,
    shouldRefresh: mode === AUTH_TIMESTAMP_VALIDATION_MODES.ON && isWithinRefreshWindow === true
  }
}

const getQueryParametersFromUrl = (url) => {
  let queryParams = {}
  let parser = new URL(url)
  for (let [key, value] of parser.searchParams.entries()) {
    queryParams[key] = value
  }
  return queryParams
}
const setupUrlChangeHandler = (callback) => {
  // Initial check of current URL
  let currentUrl = window.location.href

  // Call callback on initial page load
  callback(currentUrl)

  // Function to check URL changes
  const checkUrlChange = () => {
    const newUrl = window.location.href
    if (currentUrl !== newUrl) {
      currentUrl = newUrl
      callback(newUrl) // Call callback with new URL
    }
  }

  // Add event listener for popstate event
  window.addEventListener('popstate', checkUrlChange)

  // Pridanie event listenera na pushState a replaceState
  const originalPushState = history.pushState
  history.pushState = function () {
    originalPushState.apply(history, arguments)
    checkUrlChange()
  }

  const originalReplaceState = history.replaceState
  history.replaceState = function () {
    originalReplaceState.apply(history, arguments)
    checkUrlChange()
  }
}
const getCurrentUrl = () => window.location.href
const changeWindowLocation = (url) => {
  logAuthEvent('auth.browser.location.change', {
    targetUrl: sanitizeUrlForLog(url)
  })
  window.location.href = url
}
const ANDROID_PACKAGE_BY_SCHEME = Object.freeze({
  findeekids: 'com.findeekids.kids',
  findeeguardian: 'com.findeeguardian.app',
  findeepets: 'com.findeepets.app',
  'com.findeekids.kids': 'com.findeekids.kids',
  'com.findeekids.app': 'com.findeekids.kids',
  'com.findeeguardian.app': 'com.findeeguardian.app',
  'com.findeepets.app': 'com.findeepets.app'
})

const isAndroidWebView = () =>
  typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')

/**
 * Chrome Custom Tabs often ignore plain custom-scheme navigations
 * (`findeekids://...`). An Android intent:// URL reliably opens the app.
 */
const buildAndroidIntentRedirectUrl = (deepLinkUrl) => {
  const match = String(deepLinkUrl || '').match(/^([a-z0-9+.-]+):\/\/(.+)$/i)
  if (!match) return null

  const [, scheme, rest] = match
  if (/^https?$/i.test(scheme)) return null

  const packageName = ANDROID_PACKAGE_BY_SCHEME[scheme]
  if (!packageName) return null

  return `intent://${rest}#Intent;scheme=${scheme};package=${packageName};end`
}

const navigateToAppRedirectUrl = (url, eventName = 'auth.redirect.to-app') => {
  const isAndroid = isAndroidWebView()
  const intentUrl = isAndroid ? buildAndroidIntentRedirectUrl(url) : null
  const targetUrl = intentUrl || url

  logAuthEvent(eventName, {
    targetUrl: sanitizeUrlForLog(targetUrl),
    originalUrl: sanitizeUrlForLog(url),
    usedAndroidIntent: Boolean(intentUrl),
    isAndroid
  })

  window.location.href = targetUrl
}

const redirectToAppUrlWithAuthorizationCode = (code) => {
  const url = `${getRedirectUrl()}/${AUTH_CONFIG.authAppPath}?code=${code}`
  // const url = `${AUTH_CONFIG.appUrl}/${AUTH_CONFIG.authAppPath}?code=${code}`
  logAuthEvent('auth.redirect.to-app.prepare', {
    targetUrl: sanitizeUrlForLog(url),
    hasAuthorizationCode: Boolean(code)
  })
  navigateToAppRedirectUrl(url, 'auth.redirect.to-app')
}
const getAppAuthUrl = () => getRedirectUrl() + '/' + AUTH_CONFIG.authAppPath
// const getAppAuthUrl = () => AUTH_CONFIG.appUrl + '/' + AUTH_CONFIG.authAppPath
const redirectToAppUrl = () => {
  navigateToAppRedirectUrl(getRedirectUrl(), 'auth.redirect.to-app-root')
}
// const redirectToAppUrl = () => window.location.href = AUTH_CONFIG.appUrl
const executeHooks = async (hook, shouldContinue = () => true) => {
  for (const key of Object.keys(AUTH_HOOKS[hook])) {
    if (!shouldContinue()) return
    try {
      await AUTH_HOOKS[hook][key]()
    } catch (e) {
      logAuthEvent(
        'auth.hook.failed',
        {
          hook,
          hookId: key,
          error: summarizeErrorForLog(e)
        },
        'error'
      )
    }
  }
}

const addHook = (hook, id, callback) => {
  if (!id) {
    id = Math.random().toString(36).substring(7)
  }
  AUTH_HOOKS[hook][id] = callback
}

const Authenticator = {
  state: {
    isInstalled: computed(() => isInstalled.value),
    isLoggedIn: computed(() => isLoggedIn.value),
    hasError: computed(() => hasError.value),
    errorMessage: computed(() => errorMessage.value),
    isLoading: computed(() => isLoading.value)
  },
  tokens: {
    getSessionId,
    getRefreshToken: () => storage.getItem(AUTH_CONFIG.refreshTokenStorageKey),
    getAccessToken: () => storage.getItem(AUTH_CONFIG.accessTokenStorageKey),
    getTimestampValidationMode: () => getTimestampValidationMode(),
    getAccessTokenExpirationMs: () =>
      getJwtExpirationMs(storage.getItem(AUTH_CONFIG.accessTokenStorageKey)),
    getAccessTokenTimestampValidation: (bufferMs = 0) =>
      getAccessTokenTimestampValidation(bufferMs),
    shouldRefreshAccessToken: (bufferMs = 0) => {
      return getAccessTokenTimestampValidation(bufferMs).shouldRefresh
    },
    clearTokens: (debug) => {
      logAuthEvent('auth.tokens.cleared', {
        reason: debug,
        hadAccessToken: Boolean(storage.getItem(AUTH_CONFIG.accessTokenStorageKey)),
        hadRefreshToken: Boolean(storage.getItem(AUTH_CONFIG.refreshTokenStorageKey))
      })
      tokenRevision += 1
      authSessionRevision += 1
      storage.removeItem(AUTH_CONFIG.accessTokenStorageKey)
      storage.removeItem(AUTH_CONFIG.refreshTokenStorageKey)
    },
    saveTokens: (accessToken, refreshToken) => {
      if (accessToken || refreshToken) tokenRevision += 1
      if (accessToken) storage.setItem(AUTH_CONFIG.accessTokenStorageKey, accessToken)
      if (refreshToken) storage.setItem(AUTH_CONFIG.refreshTokenStorageKey, refreshToken)

      logAuthEvent('auth.tokens.saved', {
        accessTokenInfo: buildTokenMetadataForLog(accessToken),
        refreshTokenInfo: buildTokenMetadataForLog(refreshToken)
      })
    },
    hasTokens: () =>
      storage.getItem(AUTH_CONFIG.accessTokenStorageKey) &&
      storage.getItem(AUTH_CONFIG.refreshTokenStorageKey)
  },
  hooks: {
    removeHook: (hook, id) => {
      if (!AUTH_HOOKS[hook][id]) return
      delete AUTH_HOOKS[hook][id]
    },
    afterLogin: (callback, id = null) => addHook('afterLogin', id, callback),
    afterLogout: (callback, id = null) => addHook('afterLogout', id, callback),
    beforeLogout: (callback, id = null) => addHook('beforeLogout', id, callback),
    onTokenRefreshFailed: (callback, id = null) => addHook('onTokenRefreshFailed', id, callback)
  },
  actions: {
    getAuthUrl: (type, lang = 'cs-CZ', appName = DEFAULT_APP_TYPE) => {
      setAppType(appName)
      const canonicalAppName = String(appType.value || DEFAULT_APP_TYPE).toUpperCase()
      const session = uuidv4()
      if (type === 'login') type = ''
      // Strip trailing slashes from the configured auth host so that values like
      // `https://identity.example.com/` and `https://identity.example.com` both
      // produce `https://identity.example.com/register?...` instead of the
      // broken `https://identity.example.com//register?...` seen in test builds.
      const authBase = String(AUTH_CONFIG.authUrl || '').replace(/\/+$/, '')
      const authUrl = `${authBase}/${type}?lang=${lang}&session=${session}&${APP_TYPE_QUERY_PARAM}=${canonicalAppName}`

      logAuthEvent('auth.url.generated', {
        type: type || 'login',
        language: lang,
        appName: canonicalAppName,
        targetUrl: sanitizeUrlForLog(authUrl)
      })

      return authUrl
    },
    handleLogin: async () => {
      logAuthEvent('auth.handle-login.started')
      if (!Authenticator.checkIfPluginIsNotInstalled()) return

      isLoading.value = true
      try {
        // If init already restored a previous session (e.g. from localStorage) and the app
        // was opened on `handle-login?code=...`, we must clear that session before
        // exchanging the new code. Otherwise `afterLogin` runs twice and
        // `startLoadingUserSettings` short-circuits (interval already running), leaving
        // the new user with stale `userStore` data.
        if (isLoggedIn.value) {
          logAuthEvent('auth.handle-login.clearing-stale-session', {
            hadExistingSession: true
          })
          try {
            await executeHooks('beforeLogout')
          } catch (e) {
            logAuthEvent(
              'auth.handle-login.clearing-stale-session.before-logout-failed',
              {
                error: summarizeErrorForLog(e)
              },
              'error'
            )
          }
          Authenticator.tokens.clearTokens('pre-handle-login')
          isLoggedIn.value = false
          try {
            await executeHooks('afterLogout')
          } catch (e) {
            logAuthEvent(
              'auth.handle-login.clearing-stale-session.after-logout-failed',
              {
                error: summarizeErrorForLog(e)
              },
              'error'
            )
          }
        }

        const currentUrl = getCurrentUrl()
        const queryParams = getQueryParametersFromUrl(currentUrl)

        const authorizationCode = queryParams[AUTH_CONFIG.authorizationTokenUrlParamKey]

        logAuthEvent('auth.handle-login.url-processed', {
          currentUrl: sanitizeUrlForLog(currentUrl),
          queryKeys: Object.keys(queryParams).sort().join(','),
          hasAuthorizationCode: Boolean(authorizationCode)
        })

        if (!authorizationCode) {
          hasError.value = true
          errorMessage.value = 'No authorization code found'
          logAuthEvent(
            'auth.handle-login.missing-code',
            {
              currentUrl: sanitizeUrlForLog(currentUrl)
            },
            'warn'
          )
          await Authenticator.actions.handleLogout(() => redirectToAppUrl())
          return
        }

        Authenticator.clearErrorState()
        logAuthEvent('auth.handle-login.token-exchange.started', {
          hasAuthorizationCode: true
        })

        // receive tokens from server
        const { data, error, statusCode } = await getAccessRefreshTokenByAuthorizationCode({
          code: authorizationCode
        })

        if (error.value || statusCode.value !== 200) {
          hasError.value = true
          errorMessage.value = 'Error receiving tokens from server'
          logAuthEvent(
            'auth.handle-login.token-exchange.failed',
            {
              statusCode: statusCode.value,
              error: summarizeErrorForLog(error.value)
            },
            'error'
          )
          // Reset auth state and leave the temporary callback route.
          await Authenticator.actions.handleLogout(() => redirectToAppUrl())
          return
        }

        authSessionRevision += 1
        Authenticator.tokens.saveTokens(data.value.accessToken, data.value.refreshToken)
        isLoggedIn.value = true

        logAuthEvent('auth.handle-login.completed', {
          accessTokenInfo: buildTokenMetadataForLog(data.value.accessToken),
          refreshTokenInfo: buildTokenMetadataForLog(data.value.refreshToken)
        })

        await executeHooks('afterLogin')
      } finally {
        isLoading.value = false
      }
    },
    handleLogout: async (callback = null) => {
      authSessionRevision += 1
      // Stop an in-flight refresh from rotating credentials during intentional sign-out.
      if (isInstalled.value) tokenRevision += 1
      const session = captureTokenSession()
      logoutSession = session
      isLoading.value = true
      logAuthEvent('auth.handle-logout.started')

      // Everything below is wrapped in try/finally so `isLoading` is ALWAYS reset,
      // even if any hook, the signOut call, or the callback throws / hangs-then-rejects.
      // Leaving `isLoading` as `true` leaves the whole app stuck on the boot spinner
      // (see packages/findee/CHANGELOG.md "Loading state stuck after token refresh failure").
      try {
        try {
          await executeHooks('beforeLogout', () => isCurrentTokenSession(session))
        } catch (beforeLogoutError) {
          logAuthEvent(
            'auth.handle-logout.before-logout-hooks-failed',
            {
              error: summarizeErrorForLog(beforeLogoutError)
            },
            'error'
          )
        }

        if (!isCurrentTokenSession(session)) return

        // Startup rejection still ends the local session before the plugin is installed.
        if (!Authenticator.checkIfPluginIsNotInstalled()) {
          Authenticator.tokens.clearTokens('logout-during-init')
          isLoggedIn.value = false
          return
        }

        const accessToken = session.accessToken
        if (!accessToken) {
          logAuthEvent('auth.handle-logout.no-access-token', {
            callbackProvided: Boolean(callback)
          })
        } else {
          try {
            const { error } = await signOutUser({ accessToken })
            if (error.value) {
              hasError.value = true
              errorMessage.value = error.value
              logAuthEvent(
                'auth.handle-logout.server-failed',
                {
                  error: summarizeErrorForLog(error.value)
                },
                'error'
              )
            }
          } catch (signOutError) {
            hasError.value = true
            errorMessage.value = signOutError?.message || 'signOutUser failed'
            logAuthEvent(
              'auth.handle-logout.server-failed',
              {
                error: summarizeErrorForLog(signOutError)
              },
              'error'
            )
          }
        }

        // A replacement login must survive an old sign-out request finishing late.
        if (!isCurrentTokenSession(session)) return

        Authenticator.tokens.clearTokens(1)
        isLoggedIn.value = false
        const clearedSession = captureTokenSession()

        try {
          await executeHooks('afterLogout', () => isCurrentTokenSession(clearedSession))
        } catch (afterLogoutError) {
          logAuthEvent(
            'auth.handle-logout.after-logout-hooks-failed',
            {
              error: summarizeErrorForLog(afterLogoutError)
            },
            'error'
          )
        }

        if (!isCurrentTokenSession(clearedSession)) return

        if (callback) {
          try {
            callback()
          } catch (callbackError) {
            logAuthEvent(
              'auth.handle-logout.callback-failed',
              {
                error: summarizeErrorForLog(callbackError)
              },
              'error'
            )
          }
        }

        logAuthEvent('auth.handle-logout.completed', {
          callbackProvided: Boolean(callback)
        })
      } finally {
        if (logoutSession === session) logoutSession = null
        isLoading.value = false
      }
    },
    refreshTokens: async (skipInstallCheck = false) => {
      if (!skipInstallCheck && !Authenticator.checkIfPluginIsNotInstalled()) return -1
      const token = Authenticator.tokens.getRefreshToken()
      if (!token) return -1
      if (logoutSession && isCurrentTokenSession(logoutSession)) return -1
      const session = captureTokenSession()

      // Startup and API/native resume must not rotate the same refresh token concurrently.
      if (tokenRefreshFlight && isCurrentTokenSession(tokenRefreshFlight.session)) {
        return tokenRefreshFlight.promise
      }

      const flight = { session, promise: null }
      tokenRefreshFlight = flight
      flight.promise = Promise.resolve().then(async () => {
        logAuthEvent('auth.refresh.started', {
          skipInstallCheck,
          hasRefreshToken: true
        })
        try {
          const { data, statusCode, error, aborted } = await refreshTokensCall(
            encodeURIComponent(token)
          )

          // An old request must not restore or invalidate a session that has since changed.
          if (!isCurrentTokenSession(session)) return -1

          // Generic HTTP 400 can be a request/proxy failure, not a rejected refresh token.
          if (
            (statusCode?.value === 401 ||
              (statusCode?.value === 400 && data?.value?.code === 'InvalidCode')) &&
            !isInterruptedRefreshResponse(error, aborted)
          ) {
            logAuthEvent(
              'auth.refresh.failed.invalid-token',
              { statusCode: statusCode.value },
              'warn'
            )
            await executeHooks('onTokenRefreshFailed', () => isCurrentTokenSession(session))
            if (!isCurrentTokenSession(session) && hasStoredCredentials()) return -1
            return false
          }

          if (statusCode?.value === 200 && !error?.value && hasValidTokenPair(data?.value)) {
            // Persist rotation before releasing any startup, API or native caller.
            Authenticator.tokens.saveTokens(data.value.accessToken, data.value.refreshToken)
            logAuthEvent('auth.refresh.completed', {
              statusCode: statusCode.value,
              accessTokenInfo: buildTokenMetadataForLog(data.value.accessToken),
              refreshTokenInfo: buildTokenMetadataForLog(data.value.refreshToken)
            })
            return data.value
          }

          // HTTP 200 can arrive before its body times out or fails JSON parsing.
          // Neither that nor an unavailable server proves that the session is invalid.
          logAuthEvent(
            'auth.refresh.failed.temporary',
            { statusCode: statusCode?.value ?? null },
            'warn'
          )
          return -1
        } catch (error) {
          logAuthEvent(
            'auth.refresh.failed.temporary',
            { error: summarizeErrorForLog(error) },
            'warn'
          )
          return -1
        }
      })

      try {
        return await flight.promise
      } finally {
        if (tokenRefreshFlight === flight) tokenRefreshFlight = null
      }
    }
  },
  init: (config) => {
    Authenticator.setSettings(config)

    logAuthEvent('auth.init.started', {
      autoAuth: AUTH_CONFIG.autoAuth,
      authAppPath: AUTH_CONFIG.authAppPath,
      authUrl: sanitizeUrlForLog(AUTH_CONFIG.authUrl),
      redirectUrl: sanitizeUrlForLog(getRedirectUrl()),
      hasStoredTokens: Boolean(Authenticator.tokens.hasTokens()),
      timestampValidationMode: getTimestampValidationMode()
    })

    const load = async () => {
      isLoading.value = true

      if (!hasStoredCredentials()) {
        logAuthEvent('auth.init.no-stored-tokens')
        return
      }

      logAuthEvent('auth.init.refresh-existing-session.started')

      const session = captureTokenSession()
      const tokensRefreshed = await Authenticator.actions.refreshTokens(true)

      if (tokensRefreshed === false && isCurrentTokenSession(session)) {
        logAuthEvent(
          'auth.init.refresh-existing-session.failed',
          {
            hasTokensRefreshed: Boolean(tokensRefreshed)
          },
          'warn'
        )
        Authenticator.tokens.clearTokens(2)
        isLoggedIn.value = false
        return
      }

      if (!hasStoredCredentials()) return

      isLoggedIn.value = true
      if (!hasValidTokenPair(tokensRefreshed)) {
        logAuthEvent('auth.init.refresh-existing-session.skipped', {
          reason: 'temporary-refresh-error'
        })
        await executeHooks('afterLogin')
        return
      }

      logAuthEvent('auth.init.refresh-existing-session.completed', {
        accessTokenInfo: buildTokenMetadataForLog(tokensRefreshed.accessToken),
        refreshTokenInfo: buildTokenMetadataForLog(tokensRefreshed.refreshToken)
      })
      await executeHooks('afterLogin')
    }

    load().finally(() => {
      isLoading.value = false
      isInstalled.value = true
      logAuthEvent('auth.init.completed', {
        autoAuth: AUTH_CONFIG.autoAuth
      })

      if (!AUTH_CONFIG.autoAuth) return

      setupUrlChangeHandler(async (url) => {
        if (!url.includes(AUTH_CONFIG.authAppPath)) return

        logAuthEvent('auth.auto-auth.route-detected', {
          currentUrl: sanitizeUrlForLog(url)
        })

        await Authenticator.actions.handleLogin()
      })
    })
  },
  checkIfPluginIsInstalled: () => {
    if (!isInstalled.value) return true
    logAuthEvent('auth.init.duplicate', {}, 'error')
    return false
  },
  checkIfPluginIsNotInstalled: () => {
    if (isInstalled.value) return true
    logAuthEvent('auth.init.not-ready', {}, 'error')
    return false
  },
  setInitialState: () => {
    isLoading.value = true
    isLoggedIn.value = false
    hasError.value = false
    errorMessage.value = ''
  },
  clearErrorState: () => {
    hasError.value = false
    errorMessage.value = ''
  },
  setSettings: (config = {}) => {
    const removeThisFunction = () => setTimeout(() => delete Authenticator['setSettings'])
    if (Object.keys(config).length === 0) return removeThisFunction()
    if (hasOwnConfig(config, 'storageNamespace')) {
      setAuthStorageNamespace(config.storageNamespace)
    }
    if (config.authApiUrl) AUTH_CONFIG.authApiUrl = config.authApiUrl
    if (config.appUrl) AUTH_CONFIG.appUrl = config.appUrl
    if (config.authUrl) AUTH_CONFIG.authUrl = config.authUrl
    if (config.authAppPath) AUTH_CONFIG.authAppPath = config.authAppPath
    if (hasOwnConfig(config, 'autoAuth')) AUTH_CONFIG.autoAuth = config.autoAuth
    if (hasOwnConfig(config, 'enableTimestampValidation')) {
      AUTH_CONFIG.timestampValidationMode = config.enableTimestampValidation
        ? AUTH_TIMESTAMP_VALIDATION_MODES.ON
        : AUTH_TIMESTAMP_VALIDATION_MODES.OFF
    }
    if (hasOwnConfig(config, 'timestampValidationMode')) {
      AUTH_CONFIG.timestampValidationMode = normalizeTimestampValidationMode(
        config.timestampValidationMode
      )
    }
    logAuthEvent('auth.settings.updated', {
      autoAuth: AUTH_CONFIG.autoAuth,
      authAppPath: AUTH_CONFIG.authAppPath,
      authUrl: sanitizeUrlForLog(AUTH_CONFIG.authUrl),
      redirectUrl: sanitizeUrlForLog(getRedirectUrl()),
      timestampValidationMode: AUTH_CONFIG.timestampValidationMode,
      storageNamespace: AUTH_CONFIG.storageNamespace
    })
    removeThisFunction()
  }
}

const AuthenticatorApp = {
  useState: () => ({
    ...Authenticator.state
  }),
  useActions: () => ({
    ...Authenticator.actions
  }),
  useHooks: () => ({
    ...Authenticator.hooks
  }),
  tokens: {
    getSessionId: () => Authenticator.tokens.getSessionId(),
    getRefreshToken: () => Authenticator.tokens.getRefreshToken(),
    getAccessToken: () => Authenticator.tokens.getAccessToken(),
    getTimestampValidationMode: () => Authenticator.tokens.getTimestampValidationMode(),
    getAccessTokenExpirationMs: () => Authenticator.tokens.getAccessTokenExpirationMs(),
    getAccessTokenTimestampValidation: (bufferMs) =>
      Authenticator.tokens.getAccessTokenTimestampValidation(bufferMs),
    shouldRefreshAccessToken: (bufferMs) => Authenticator.tokens.shouldRefreshAccessToken(bufferMs),
    saveTokens: (accessToken, refreshToken) =>
      Authenticator.tokens.saveTokens(accessToken, refreshToken)
  }
}

Authenticator.setInitialState()

export {
  Authenticator,
  changeWindowLocation,
  redirectToAppUrlWithAuthorizationCode,
  redirectToAppUrl,
  getAppAuthUrl
}
export default AuthenticatorApp
